import type { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import type { TLocalize } from '@hooks/use-localize';
import { usePostalCodeLookup } from '@hooks/register/use-postal-code-lookup';
import type { INowFormValues, ISelectOption } from '@/types/now';
import FieldError from '@components/field-error';

interface IFieldProps {
    t: TLocalize;
    className?: string;
}

const useNowForm = () => useFormContext<INowFormValues>();

export const FirstNameField = ({ t, className = 'form-group' }: IFieldProps) => {
    const { register, formState: { errors } } = useNowForm();

    return (
        <div className={className}>
            <label htmlFor="FirstName">
                <FieldError required message={errors.firstName?.message} />{' '}
                {t('Word_FirstName', 'First Name')}{' '}
                <span>
                    <em style={{ fontSize: 'smaller' }}>
                        {t('DashLabel_AsItAppearsOnStateIssuedId', 'as it appears on State-Issued ID')}
                    </em>
                </span>
            </label>
            <input id="FirstName" className="form-control trim-field" maxLength={32} {...register('firstName')} />
        </div>
    );
};

export const LastNameField = ({ t, className = 'form-group' }: IFieldProps) => {
    const { register, formState: { errors } } = useNowForm();

    return (
        <div className={className}>
            <label htmlFor="LastName">
                <FieldError required message={errors.lastName?.message} />{' '}
                {t('Word_LastName', 'Last Name')}{' '}
                <span>
                    <em style={{ fontSize: 'smaller' }}>
                        {t('DashLabel_AsItAppearsOnStateIssuedId', 'as it appears on State-Issued ID')}
                    </em>
                </span>
            </label>
            <input id="LastName" className="form-control trim-field" maxLength={32} {...register('lastName')} />
        </div>
    );
};

export const EmailField = ({ t, className = 'form-group' }: IFieldProps) => {
    const { register, formState: { errors } } = useNowForm();

    return (
        <div className={className}>
            <label htmlFor="Email">
                <FieldError required message={errors.email?.message} /> {t('DashLabel_Email', 'Email')}
            </label>
            <input id="Email" className="form-control" maxLength={64} {...register('email')} />
        </div>
    );
};

export const ResidesInUsaField = ({ t, className = 'form-group' }: IFieldProps) => {
    const { register, formState: { errors } } = useNowForm();

    return (
        <div className={className}>
            <label htmlFor="IsResidingInUsa">
                <FieldError required message={errors.isResidingInUsa?.message} />{' '}
                {t('DashLabel_DoYouResideInTheUnitedStates', 'Do you reside in the United States')}?
            </label>
            <select id="IsResidingInUsa" className="form-control" {...register('isResidingInUsa')}>
                <option value="">{t('DashLabel_PleaseSelect', 'Please Select')}</option>
                <option value="1">{t('Word_Yes', 'Yes')}</option>
                <option value="0">{t('Word_No', 'No')}</option>
            </select>
        </div>
    );
};

interface IResidenceSection {
    id: string;
    show: boolean;
    children: ReactNode;
}

export const ResidenceSection = ({
    id,
    show,
    children,
}: IResidenceSection) => {
    if (!show) return null;

    return (
        <div id={id} className="sub-question sub-question-no-rule">
            {children}
        </div>
    );
};

export const CountryOfResidenceField = ({
    t,
    className = 'form-group',
    countries,
}: IFieldProps & { countries: ISelectOption[] }) => {
    const { register, formState: { errors } } = useNowForm();

    return (
        <div className={className}>
            <label htmlFor="CountryOfResidenceId">
                <FieldError required message={errors.countryOfResidenceId?.message} />{' '}
                {t('DashLabel_CountryOfResidence', 'Country of Residence')}:
            </label>
            <select id="CountryOfResidenceId" className="form-control" {...register('countryOfResidenceId')}>
                <option value="">{t('DashLabel_PleaseSelect', 'Please Select')}</option>
                {countries.map((country) => (
                    <option key={country.value} value={country.value}>
                        {country.label}
                    </option>
                ))}
            </select>
        </div>
    );
};

export const InternationalPhoneField = ({ t, className = 'form-group' }: IFieldProps) => {
    const { register, formState: { errors } } = useNowForm();

    return (
        <div className={className}>
            <label htmlFor="InternationalPhoneNumber">
                <FieldError required message={errors.internationalPhoneNumber?.message} />{' '}
                {t('DashLabel_PhoneNumber', 'Phone Number')}:
            </label>
            <input
                id="InternationalPhoneNumber"
                className="form-control trim-field"
                maxLength={20}
                {...register('internationalPhoneNumber')}
            />
        </div>
    );
};

export const AddressLine1Field = ({ t, className = 'form-group' }: IFieldProps) => {
    const { register, formState: { errors } } = useNowForm();

    return (
        <div className={className}>
            <label htmlFor="AddressLine1">
                <FieldError required message={errors.addressLine1?.message} />{' '}
                {t('DashLabel_StreetAddress', 'Street Address')}{' '}
                <span>
                    <em style={{ fontSize: 'smaller' }}>{t('DashLabel_NoPoBox', 'no PO Box')}</em>
                </span>
            </label>
            <input id="AddressLine1" className="form-control trim-field" maxLength={64} {...register('addressLine1')} />
        </div>
    );
};

export const CityField = ({ t, className = 'form-group' }: IFieldProps) => {
    const { register, formState: { errors } } = useNowForm();

    return (
        <div className={className}>
            <label htmlFor="City">
                <FieldError required message={errors.city?.message} /> {t('Word_City', 'City')}:
            </label>
            <input id="City" className="form-control trim-field" maxLength={32} {...register('city')} />
        </div>
    );
};

export const StateField = ({
    t,
    className = 'form-group',
    states,
}: IFieldProps & { states: ISelectOption[] }) => {
    const { register, formState: { errors } } = useNowForm();

    return (
        <div className={className}>
            <label htmlFor="State">
                <FieldError required message={errors.state?.message} /> {t('Word_State', 'State')}:
            </label>
            <select id="State" className="form-control" {...register('state')}>
                <option value="">{t('DashLabel_PleaseSelect', 'Please Select')}</option>
                {states.map((state) => (
                    <option key={state.value} value={state.value}>
                        {state.label}
                    </option>
                ))}
            </select>
        </div>
    );
};

export const PostalCodeField = ({ t, className = 'form-group' }: IFieldProps) => {
    const { register, formState: { errors } } = useNowForm();

    const lookupPostalCode = usePostalCodeLookup();

    const { onBlur, ...field } = register('postalCode');

    return (
        <div className={className}>
            <label htmlFor="PostalCode">
                <FieldError required message={errors.postalCode?.message} />{' '}
                {t('DashLabel_PostalCode', 'Zip/Postal Code')}:
            </label>
            <input
                id="PostalCode"
                className="form-control trim-field"
                maxLength={10}
                {...field}
                onBlur={(event) => {
                    onBlur(event);
                    lookupPostalCode();
                }}
            />
        </div>
    );
};

export const MobilePhoneField = ({ t, className = 'form-group' }: IFieldProps) => {
    const { register, formState: { errors } } = useNowForm();

    return (
        <div className={className}>
            <label htmlFor="MobileAuthPhone">
                <FieldError required message={errors.mobileAuthPhone?.message} />{' '}
                {t('DashLabel_USMobilePhone', 'U.S. Mobile Phone:')}
            </label>
            <div className="input-group">
                <span className="input-group-addon">+1</span>
                <input id="MobileAuthPhone" className="form-control" maxLength={10} {...register('mobileAuthPhone')} />
            </div>
        </div>
    );
};

export const DonationTypeField = ({
    t,
    className = 'form-group',
    donationTypes,
}: IFieldProps & { donationTypes: ISelectOption[] }) => {
    const { register, formState: { errors } } = useNowForm();

    return (
        <div className={className}>
            <label htmlFor="DonationType">
                <FieldError message={errors.donationType?.message} />{' '}
                {t('DashLabel_WhatTypeDonationAreYouConsidering', 'What type of donation are you considering')}?
            </label>
            <select id="DonationType" className="form-control" {...register('donationType')}>
                <option value="">{t('DashLabel_PleaseSelect', 'Please Select')}</option>
                {donationTypes.map((donationType) => (
                    <option key={donationType.value} value={donationType.value}>
                        {donationType.label}
                    </option>
                ))}
            </select>
        </div>
    );
};

export const HipaaField = ({
    t,
    className = 'form-group well well-sm',
    labelClassName = 'radio-inline',
}: IFieldProps & { labelClassName?: string }) => {
    const { register, formState: { errors } } = useNowForm();

    return (
        <div className={className}>
            <div className="label-style">
                <strong>{t('DashLabel_HIPAARelease', 'HIPAA Release')}</strong>{' '}
                <FieldError required message={errors.agreedToHipaa?.message} />
            </div>
            <div className="hipaa-release">
                <label className={labelClassName}>
                    <input type="checkbox" {...register('agreedToHipaa')} />{' '}
                    <span>
                        {t('DashLabel_ByCheckingThis', 'By checking this')}{' '}
                        <a
                            href="/register/hipaa-release"
                            className="hipaa-release-link"
                            target="_blank"
                            rel="noreferrer"
                        >
                            {t('DashLabel_HIPAARelease', 'HIPAA Release')}
                        </a>
                        ,{' '}
                        {t(
                            'DashLabel_YouAgreeToAllowUsToStoreYourInformationAndToContactTheCenter',
                            'you agree to allow us to store your information and use it to facilitate kidney donation. We will never ask you to submit sensitive personal information via e-mail or text messages. If you receive solicitations for your SSN or other personal information via a text message or e-mail, please report it to us immediately.',
                        )}
                        .
                    </span>
                </label>
            </div>
        </div>
    );
};

export const DonorMentorOptInField = ({ t, className = 'form-group' }: IFieldProps) => {
    const { register, formState: { errors } } = useNowForm();

    return (
        <div className={className}>
            <label>
                <FieldError required message={errors.connectToDonorIsDesired?.message} />{' '}
                {t(
                    'DashLabel_DonorConnectOptInQuestion',
                    'Would you like us to connect you with a living donor mentor who can answer your questions and help you better understand the process?',
                )}
            </label>

            <div>
                <label className="radio-inline">
                    <input type="radio" id="ConnectYes" value="1" {...register('connectToDonorIsDesired')} />{' '}
                    {t('Word_Yes', 'Yes')}
                </label>

                <label className="radio-inline">
                    <input type="radio" id="ConnectNo" value="0" {...register('connectToDonorIsDesired')} />{' '}
                    {t('Label_NotAtThisTime', 'Not at this time')}
                </label>
            </div>
        </div>
    );
};

export const TextOptInNotice = ({ t, className }: IFieldProps) => (
    <div className={className ?? 'alert alert-warning'}>
        {t(
            'DashNotice_TextingOptIn',
            'By providing your mobile number, you are agreeing to allow us to communicate with you about your registration via text/calls for the purposes of authenticating your registration and helping you with the organ donation process.',
        )}
    </div>
);
